import { useInternational } from '@/common/locale';
import { computed } from 'vue';
import { useLogsKeywordSettingStore } from '@/logs/stores/useKeywordSettingModal';
import { storeToRefs } from 'pinia';
import { LogAlertInfo } from '@/alert/components/logAlertViewerWindow/logAlertViewerWindow.types';
import {
  getAddedKeysHighlightInfo,
  getFilteredKeysHighlightInfo,
  highlightLog,
} from '@/logs/utils/logMessage.utils';

import { useGetV8LogAlertMessageByAlertRuleIdAndTargetId } from '@/api-client/log/queries/logging-view-v8-controller/logging-view-v8-controller';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import { useVueQueryErrorHandler } from '@/common/composables/useVueQueryErrorHandler';

export interface Props {
  isShow: boolean;
  targetId: string;
  ruleId: string;
  resultId?: string;
  fromTime?: string; // fromTime,toTime UTC
  toTime?: string;
}
export interface Emit {
  (e: 'update:isShow', visible: boolean): void;
}

const INIT_LOG_INFO = {
  alertRuleId: '',
  filteredKeys: [],
  logType: '',
  path: '',
  pathId: '',
  pathName: '',
  targetId: '',
  targetKind: '',
  targetName: '',
};

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const { configKeywordList, userKeywordList } = storeToRefs(useLogsKeywordSettingStore());
  const { errorHandlerWithMessage } = useVueQueryErrorHandler();

  const isShow = computed({
    get: () => props.isShow,
    set: (value) => emit('update:isShow', value),
  });

  // Query params
  const queryParams = computed(() => ({
    alertResultId: props.resultId ?? undefined,
    fromTime: props.fromTime ?? undefined,
    toTime: props.toTime ?? undefined,
  }));

  const alertRuleId = computed(() => props.ruleId);
  const targetId = computed(() => props.targetId);

  // Vue Query 훅
  const { data: queryData, isPending } = useGetV8LogAlertMessageByAlertRuleIdAndTargetId(
    alertRuleId,
    targetId,
    queryParams,
    {
      request: {
        frameName: FRAME_NAMES.ALERT_DETAIL.INFORMATION,
      },
      query: {
        meta: {
          onError: (error: unknown) => {
            errorHandlerWithMessage({ error });
          },
        },
        enabled: computed(() => props.isShow && !!props.ruleId && !!props.targetId),
        select: (response) => {
          const data = response?.data?.data ?? {};
          const filteredKeys = getFilteredKeysHighlightInfo(
            data.filteredKeys ?? [],
            configKeywordList.value,
          );
          const logAlertInfo: LogAlertInfo = {
            ...data,
            filteredKeys,
          };
          const logAlertMessageInfo = highlightLog(data?.message?.message ?? '', {
            configKeywordList: filteredKeys,
            userKeywordList: getAddedKeysHighlightInfo(
              data?.message?.message ?? '',
              userKeywordList.value,
            ),
          });

          return {
            logAlertInfo,
            logAlertMessageInfo,
          };
        },
      },
    },
  );

  const logAlertInfo = computed<LogAlertInfo>(() => {
    return queryData.value?.logAlertInfo ?? { ...INIT_LOG_INFO };
  });

  const logAlertMessageInfo = computed(() => {
    return queryData.value?.logAlertMessageInfo ?? '';
  });

  return {
    t,
    isShow,
    isLoading: isPending,
    logAlertInfo,
    logAlertMessageInfo,
  };
};
